import React, { useCallback, useMemo, useState } from 'react'
import type { WorkingDayKey, WorkingDaysProps } from './WorkingDays.types'
import {
  WORKING_DAY_KEYS,
  WORKING_DAY_LABELS,
  WORKING_DAY_TITLES,
  WORKING_DAYS_WEEKDAY_VALUE,
} from './WorkingDays.types'
import './WorkingDays.css'

const toggleDay = (
  days: WorkingDayKey[],
  day: WorkingDayKey,
): WorkingDayKey[] =>
  days.includes(day) ? days.filter((item) => item !== day) : [...days, day]

const WorkingDays: React.FC<WorkingDaysProps> = ({
  value,
  defaultValue = WORKING_DAYS_WEEKDAY_VALUE,
  interactive = false,
  disabled = false,
  darkMode = false,
  className = '',
  previewState,
  previewValue,
  onChange,
  onDayClick,
}) => {
  const [internalValue, setInternalValue] =
    useState<WorkingDayKey[]>(defaultValue)

  const isPreview = previewState != null || previewValue != null
  const isControlled = value !== undefined

  const resolvedValue = useMemo((): WorkingDayKey[] => {
    if (previewValue) {
      return previewValue
    }
    if (previewState === 'available') {
      return [...WORKING_DAY_KEYS]
    }
    if (previewState === 'unavailable') {
      return []
    }
    return isControlled ? value : internalValue
  }, [internalValue, isControlled, previewState, previewValue, value])

  const handleDayClick = useCallback(
    (day: WorkingDayKey) => {
      if (isPreview || disabled || !interactive) {
        return
      }

      const nextAvailable = !resolvedValue.includes(day)
      const nextValue = toggleDay(resolvedValue, day)

      if (!isControlled) {
        setInternalValue(nextValue)
      }

      onDayClick?.(day, nextAvailable)
      onChange?.(nextValue)
    },
    [
      disabled,
      interactive,
      isControlled,
      isPreview,
      onChange,
      onDayClick,
      resolvedValue,
    ],
  )

  const classNames = [
    'ds-working-days',
    darkMode ? 'ds-working-days--dark' : '',
    interactive ? 'ds-working-days--interactive' : '',
    isPreview ? 'ds-working-days--preview' : '',
    disabled ? 'ds-working-days--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classNames}
      role="group"
      aria-label="Working days"
      aria-disabled={disabled || undefined}
    >
      {WORKING_DAY_KEYS.map((day) => {
        const isAvailable = resolvedValue.includes(day)
        const dayClassNames = [
          'ds-working-days__day',
          isAvailable
            ? 'ds-working-days__day--available'
            : 'ds-working-days__day--unavailable',
        ].join(' ')

        if (interactive && !isPreview && !disabled) {
          return (
            <button
              key={day}
              type="button"
              className={dayClassNames}
              title={WORKING_DAY_TITLES[day]}
              aria-pressed={isAvailable}
              aria-label={`${WORKING_DAY_TITLES[day]}${
                isAvailable ? ', available' : ', unavailable'
              }`}
              onClick={() => handleDayClick(day)}
            >
              {WORKING_DAY_LABELS[day]}
            </button>
          )
        }

        return (
          <span
            key={day}
            className={dayClassNames}
            title={WORKING_DAY_TITLES[day]}
            aria-label={`${WORKING_DAY_TITLES[day]}${
              isAvailable ? ', available' : ', unavailable'
            }`}
          >
            {WORKING_DAY_LABELS[day]}
          </span>
        )
      })}
    </div>
  )
}

export default WorkingDays
